import type { CanonicalProviders } from '@/ordering/canonicalOrder'
import type { OrderTrfSignature } from '@/utils/orderTrfSignature'

/**
 * Client-side gating for the "Sign TRF" call-to-action (GEN-5028).
 *
 * These checks mirror the server's authorization on
 * ``POST /orders/{orderId}/trf/signing`` (GEN-5026) so the button is only shown
 * when a mint would actually succeed. The server remains the source of truth —
 * it re-authorizes on every mint — so this is a UX gate, not a security
 * boundary.
 */

/** Signature states from which an embedded ceremony can still be opened. */
const SIGNABLE_STATUSES: ReadonlySet<OrderTrfSignature['status']> = new Set(['sent', 'delivered'])

const trimNpi = (npi: string | null | undefined): string => (npi ?? '').trim()

/**
 * Whether the TRF signature request is currently signable: a live, non-terminal,
 * unsigned envelope that has reached ``sent``/``delivered`` and carries a vendor
 * document id. ``created`` (still preparing) and any terminal/signed state do not
 * qualify.
 */
export const isTrfSignatureSignable = (signature: OrderTrfSignature | null | undefined): boolean => {
  if (!signature) return false
  if (signature.isSigned || signature.isTerminal) return false
  if (!signature.externalDocumentId) return false
  return SIGNABLE_STATUSES.has(signature.status)
}

/**
 * The NPI of the clinician the order is attributed to, resolved exactly as
 * provider-api's ``clinician_of_record`` does: the authorizing clinician when the
 * payload names one, else the ordering provider (which on a self-submit is the
 * same person, and on a legacy payload is the only clinician present).
 */
export const clinicianOfRecordNpi = (providers: CanonicalProviders | null | undefined): string => {
  const clinician = providers?.authorizingProvider ?? providers?.orderingProvider ?? null
  return trimNpi(clinician?.npi)
}

/**
 * Whether the outstanding envelope is on the embedded (in-app) channel, so the
 * Sign TRF CTA can actually open it.
 *
 * provider-api decides the channel per order at submit and projects it onto the
 * order as ``signature.channel``. An email-channel envelope (one DocuSign mailed
 * to the signer before every order signed in-app) has no captive recipient, so
 * minting an in-app signing view for it is refused server-side.
 *
 * A missing channel reads as email, as it does everywhere else: provider-api only
 * omits it when it could not read the submitted form's stamp, and it will not
 * mint against a form it cannot confirm is embedded either.
 */
export const isEmbeddedSigningChannel = (signature: OrderTrfSignature | null | undefined): boolean =>
  signature?.channel === 'embedded'

/**
 * Whether the current user is the clinician of record. Matching the clinician's
 * NPI inherently excludes a delegate submitting on-behalf: their NPI differs from
 * the authorizing clinician's, so they never match.
 */
export const isCurrentUserClinicianOfRecord = (
  providers: CanonicalProviders | null | undefined,
  currentUserNpi: string | null | undefined
): boolean => {
  const npi = trimNpi(currentUserNpi)
  const clinicianNpi = clinicianOfRecordNpi(providers)
  return npi.length > 0 && clinicianNpi.length > 0 && npi === clinicianNpi
}

export interface SignTrfActorParams {
  signature: OrderTrfSignature | null | undefined
  providers: CanonicalProviders | null | undefined
  currentUserNpi: string | null | undefined
}

/**
 * Whether this user is the person who signs this order in the app — the
 * clinician of record, on an order whose envelopes address a captive recipient.
 *
 * Deliberately says nothing about whether there is anything to sign *right now*.
 * Both facts are properties of the order and its viewer rather than of whichever
 * envelope happens to be outstanding, so they keep holding across the gap where
 * an edit has retired one envelope and its replacement does not exist yet. That
 * gap is precisely when the update flow must decide whether to send someone to
 * the ceremony, and it has no signable envelope to consult.
 */
export const isInAppSignerOfRecord = (params: SignTrfActorParams): boolean =>
  isEmbeddedSigningChannel(params.signature) && isCurrentUserClinicianOfRecord(params.providers, params.currentUserNpi)

/**
 * Whether to render the "Sign TRF" CTA: the outstanding envelope is on the
 * embedded channel, the signature is signable, and the current user is the
 * clinician of record — so a clinician someone else submitted for gets it, and
 * the submitter does not.
 */
export const canShowSignTrfCta = (params: SignTrfActorParams): boolean =>
  isInAppSignerOfRecord(params) && isTrfSignatureSignable(params.signature)

/**
 * The order to hand off to the signing page once an update lands, or ``null`` to
 * leave the clinician on the order page.
 *
 * An edit that changed the requisition invalidates the form the clinician has to
 * attest to, which is the position submitting a new order leaves them in — so it
 * ends in the same place rather than making them watch the badge resolve and
 * find the button. Only a re-issue qualifies: an edit that left the form alone
 * has nothing new to sign, and sending someone to a ceremony for it would be a
 * detour with no destination.
 *
 * Deliberately not conditioned on the outstanding signature being signable. The
 * replacement is built asynchronously, so at this moment the only envelope on
 * record is the retired one, and what it reached says nothing about what is
 * needed now: an order whose TRF was already signed needs a fresh attestation
 * just as much as one still waiting for its first.
 *
 * Reanalysis wins, hence the ``null``: it locks the order read-only, so there is
 * nothing left to sign and the page has its own destination for that case.
 */
export const signingHandoffAfterUpdate = (
  params: SignTrfActorParams & {
    orderId: string | null | undefined
    reissueRequested: boolean
    reanalysisRequested: boolean
  }
): string | null => {
  if (!params.reissueRequested || params.reanalysisRequested) return null
  if (!params.orderId) return null
  return isInAppSignerOfRecord(params) ? params.orderId : null
}
